import React from 'react';
import { Button, Group } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { buildSessionScopeKey, clearSessionExpiry, logoutSession } from '../api/auth';
import ProgramFooter from '../components/ProgramFooter';
import ProgramHeader from '../components/ProgramHeader';
import { useProtectedSession } from '../hooks/useProtectedSession';
import type { LandingDestination, LandingPageConfig, ProgramConfig, Solution } from '../types/schema';
import { getPortalPathInfo } from '../utils/portalPath';
import NotFoundPage from './NotFoundPage';

const INK = '#2b2b2b';
const PAPER = '#faf8f5';
const LINE = '#e3ddd3';
const LOGIN_COLOR = '#0f5a56';

interface ProductSelectorPageProps {
  config: ProgramConfig;
}

function destinationPath(destination: LandingDestination): string {
  return `/${destination.client}/${destination.program}`;
}

type MultiProductSolution = Extract<Solution, 'multi-product-copay-enrollment' | 'multi-product-voucher-submission'>;

function productPath(productKey: string, solution: MultiProductSolution): string {
  const segment = solution === 'multi-product-voucher-submission' ? 'voucher-submissions' : 'enrollments';
  return `${getPortalPathInfo().basename}/${segment}/${encodeURIComponent(productKey)}`;
}

function ProductSelectorContent({
  config,
  landing,
  solution,
  sessionEmail,
  onLogout,
  isLoggingOut = false,
}: {
  config: ProgramConfig;
  landing: LandingPageConfig;
  solution: MultiProductSolution;
  sessionEmail?: string;
  onLogout?: () => void;
  isLoggingOut?: boolean;
}) {
  const products = landing.products ?? [];
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: config.theme.colors?.background ?? PAPER,
      fontFamily: 'inherit',
      color: INK,
    }}>
      <div className="product-selector-header">
        <ProgramHeader
          programName={config.theme.displayName}
          portalName={config.theme.portalName}
          showTopBar
          showTopBarLogo={config.theme.showTopBarLogo !== false}
          showTopBarPortalName={config.theme.showTopBarPortalName !== false}
          topBarLogoUrl={config.theme.topBarLogoUrl}
          topBarLogoAlt={config.theme.topBarLogoAlt}
          topBarLogoWidth={config.theme.topBarLogoWidth}
          topBarLogoHeight={config.theme.topBarLogoHeight}
          hideTitle
          mb={0}
          sessionEmail={sessionEmail}
          onLogout={onLogout}
          isLoggingOut={isLoggingOut}
        />
      </div>

      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '56px 32px',
      }}>
        <div
          className={config.theme.contentSurface ? 'product-selector-content-surface' : undefined}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
        >
          <div style={{
            display: 'flex',
            gap: 40,
            justifyContent: 'center',
            alignItems: 'stretch',
            flexWrap: 'wrap',
            maxWidth: 980,
          }}>
            {products.map((product, index) => {
              const href = productPath(product.productKey, solution);
              return (
              <React.Fragment key={product.productKey}>
                {index > 0 && <div style={{ width: 1, background: LINE, alignSelf: 'stretch', margin: '8px 0' }} />}
                <a
                  href={href}
                  style={{
                    background: '#ffffff',
                    border: `1px solid ${LINE}`,
                    borderTop: `4px solid ${product.accentColor}`,
                    width: 420,
                    padding: '40px 36px 36px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <img src={product.logoUrl} alt={product.logoAlt} style={{ maxHeight: 74, marginBottom: 28 }} />
                  <h2 style={{
                    fontFamily: "'Helvetica Neue', Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: 19,
                    margin: '0 0 18px',
                    color: INK,
                  }}>
                    {product.title}
                  </h2>
                  <span style={{
                    fontFamily: "'Helvetica Neue', Arial, sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#ffffff',
                    background: product.accentColor,
                    border: 'none',
                    padding: '13px 34px',
                    cursor: 'pointer',
                    letterSpacing: '0.3px',
                  }}>
                    {product.buttonLabel}
                  </span>
                </a>
              </React.Fragment>
              );
            })}
          </div>
        </div>
      </main>

      <ProgramFooter
        visible
        showLogo={config.theme.showFooterLogo !== false}
        logoUrl={config.theme.footerLogoUrl}
        tagline={config.theme.footerTagline}
        legalText={landing.legalText}
        prescribingInformationUrl={config.theme.prescribingInformationUrl}
      >
        {landing.loginAction && (
          <Group justify="flex-end" wrap="nowrap">
            <Button
              component="a"
              href={destinationPath(landing.loginAction.destination)}
              variant="outline"
              size="md"
              color={LOGIN_COLOR}
              className="product-selector-footer-action"
            >
              {landing.loginAction.label}
            </Button>
          </Group>
        )}
      </ProgramFooter>
    </div>
  );
}

function AuthenticatedProductSelectorPage({
  config,
  landing,
  solution,
}: {
  config: ProgramConfig;
  landing: LandingPageConfig;
  solution: MultiProductSolution;
}) {
  const navigate = useNavigate();
  const sessionScopeKey = buildSessionScopeKey(config.clientName, config.programName);
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const logoutMutation = useMutation({ mutationFn: logoutSession });

  if (sessionQuery.isLoading) {
    return null;
  }
  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: '/' }} replace />;
  }
  if (sessionQuery.isError) {
    return <NotFoundPage />;
  }
  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    }
  };
  return (
    <ProductSelectorContent
      config={config}
      landing={landing}
      solution={solution}
      sessionEmail={sessionQuery.data?.email}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
    />
  );
}

export default function ProductSelectorPage({ config }: ProductSelectorPageProps) {
  const landing = config.pages?.landing;
  if (
    !landing?.products?.length
    || (config.solution !== 'multi-product-copay-enrollment'
      && config.solution !== 'multi-product-voucher-submission')
  ) {
    return <NotFoundPage />;
  }
  if (config.authenticationRequired) {
    return <AuthenticatedProductSelectorPage config={config} landing={landing} solution={config.solution} />;
  }
  return <ProductSelectorContent config={config} landing={landing} solution={config.solution} />;
}